import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { MemberService } from '../data';
import { Member } from '../models';

@Injectable()
export class AcceptLanguageInterceptor implements HttpInterceptor {

  private member: Member = null;

  constructor(
    private translateService: TranslateService,
    private memberService: MemberService,
  ) {
    this.member = this.memberService.getUser();
  }

  /**
   * HttpInterceptor used to always set an Accept-Language header on Requests,
   * It set at least one value and a second one for POST and PUT operations.
   *
   * - The first value is the member's reading preference language.
   *    * By default it take the preferred_language value coming from LDAP
   *      Otherwise the language chosen in the translateService.
   *    * For a GET this value can be overwrite if an Accept-Language header
   *      is present in the request intercepted.
   *
   * - The second value MUST TO be present in the a POST or PUT request intercepted.
   *    Not specifying this value carries a risk of error when saving.
   *
   * @param req
   * @param next
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let lang: string[] = [];
    const settedAcceptLanguage = req.headers && req.headers.has('Accept-Language');

    // Priority given to the preferred_language of the member.
    // If it does not exist or another language has been settled in the translateService,
    // the priority is the language present in the translateService
    const priorityMemberPreferredLanguage: boolean = this.member.preferred_language
      && this.translateService.currentLang === this.member.preferred_language.toLocaleLowerCase();

    // First position : language chosen by the user to ask for (GET) content
    lang.push(priorityMemberPreferredLanguage
      ? this.member.preferred_language.toLocaleLowerCase()
      : this.translateService.currentLang,
    );

    // On GET context, if an Accept-language has been precisely defined, we overwrite the default one below
    if (req.method === 'GET' && settedAcceptLanguage) {
      lang = [req.headers.get('Accept-Language')];
    }

    // Second position : Language chosen for posted (POST) or published (PUT) content
    if ((req.method === 'POST' || req.method === 'PUT') && settedAcceptLanguage) {
      const reqAcceptLanguage = req.headers.get('Accept-Language');
      lang.push(...reqAcceptLanguage.split(','));
    }

    const headersConfig = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Accept-Language': `${lang.join(',')}`,
    };

    const request = req.clone({ setHeaders: headersConfig });
    return next.handle(request);
  }
}
